import { getInitializedAudioSequence, type Playable } from "~/lib/audioSequence";
import { buildSequencePlan, type LoadableTrack, type SequenceSourceOption } from "~/lib/audioSequencePlan";

export type { LoadableTrack } from "~/lib/audioSequencePlan";
export type StartingPositionResolver = (context: {
  playable: Playable;
  usingFallbackSequence: boolean;
  previous?: {
    duration: number;
    getOffsetForTrack: Pick<Playable, "getOffsetForTrack">["getOffsetForTrack"];
    position: number;
    usingFallbackSequence: boolean;
  };
}) => number;

export type PlayerEngineState = {
  playable: Playable | null;
  duration: number;
  volume: number;
  playing: boolean;
  ended: boolean;
  loading: boolean;
  currentSequenceId: string | null;
  usingFallbackSequence: boolean;
  error: Error | null;
};

export type PlayerEngineActions = Pick<
  PlayerEngine,
  "setPosition" | "setVolume" | "play" | "pause" | "loadSequence" | "getPosition"
>;
type Subscriber = () => void;

class PlayerEngine {
  private state: PlayerEngineState = {
    playable: null,
    duration: 0,
    volume: 1,
    playing: false,
    ended: false,
    loading: false,
    currentSequenceId: null,
    usingFallbackSequence: false,
    error: null,
  };
  private subs = new Set<Subscriber>();
  private loadingLock: Promise<Playable | null> | null = null;
  private abortController: AbortController | null = null;
  private pendingSequenceId: string | null = null;

  constructor() {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden && this.state.playing) {
          // Just notify, position will be pulled when needed
          this.notify();
        }
      });
    }
  }

  getState = () => {
    return this.state;
  };

  subscribe = (cb: Subscriber) => {
    this.subs.add(cb);
    return () => {
      this.subs.delete(cb);
    };
  };

  private notify() {
    for (const cb of this.subs) cb();
  }

  private async initializePlayable(
    sourceOptions: Array<SequenceSourceOption>,
    startingProgress: number,
    signal: AbortSignal,
  ): Promise<{ optionIndex: number; playable: Playable; usingFallbackSequence: boolean }> {
    let lastError: unknown = new Error("No audio source options were available.");
    for (const [optionIndex, option] of sourceOptions.entries()) {
      try {
        const playable = await getInitializedAudioSequence(option.sources, startingProgress, signal);
        return { optionIndex, playable, usingFallbackSequence: option.usingFallbackSequence };
      } catch (error) {
        lastError = error;
        if (signal.aborted) throw error;
        if (optionIndex < sourceOptions.length - 1) {
          console.warn("[PlayerEngine] Audio sequence failed; retrying the next source option.", {
            sources: option.sources,
            nextSources: sourceOptions[optionIndex + 1]?.sources,
            error,
          });
        }
      }
    }
    throw lastError;
  }

  private activatePlayable(
    playable: Playable,
    sourceOptions: Array<SequenceSourceOption>,
    optionIndex: number,
    signal: AbortSignal,
    sequenceId: string,
    resolveStartingPosition?: StartingPositionResolver,
  ) {
    playable.onEnded = () => {
      this.state.ended = true;
      this.state.playing = false;
      this.notify();
    };

    playable.onSectionEnded = () => {
      this.notify();
    };

    playable.onPlaybackError = (error) => {
      void this.recoverPlaybackError(
        error,
        sourceOptions,
        optionIndex + 1,
        signal,
        sequenceId,
        resolveStartingPosition,
      );
    };

    playable.setVolume(this.state.volume);
    this.state.playable = playable;
    this.state.currentSequenceId = sequenceId;
    this.state.duration = playable.getDuration();
    this.state.volume = playable.getVolume();
    this.state.playing = false;
    this.state.ended = false;
    this.state.loading = false;
    this.state.error = null;
    this.state.usingFallbackSequence = sourceOptions[optionIndex]?.usingFallbackSequence ?? false;
    this.notify();
  }

  private async recoverPlaybackError(
    error: Error,
    sourceOptions: Array<SequenceSourceOption>,
    nextOptionIndex: number,
    signal: AbortSignal,
    sequenceId: string,
    resolveStartingPosition?: StartingPositionResolver,
  ) {
    const current = this.state.playable;
    if (!current || signal.aborted || this.state.loading || this.state.currentSequenceId !== sequenceId) return;
    if (nextOptionIndex >= sourceOptions.length) {
      this.state.playing = false;
      this.state.error = error;
      this.notify();
      return;
    }

    const duration = current.getDuration();
    const currentPosition = current.getPosition();
    const progress = duration > 0 ? (currentPosition / duration) * 100 : 0;
    // AudioSequence.destroy clears its tracks; recovery needs their offsets afterward.
    const previousTrackOffsets = Array.from(
      { length: sourceOptions[nextOptionIndex - 1].sources.length + 1 },
      (_, index) => current.getOffsetForTrack(index),
    );
    const previousUsingFallbackSequence = this.state.usingFallbackSequence;
    const resumePlayback = this.state.playing;
    this.state.loading = true;
    this.state.playing = false;
    current.destroy();
    this.state.playable = null;
    this.notify();

    try {
      const recovered = await this.initializePlayable(
        sourceOptions.slice(nextOptionIndex),
        resolveStartingPosition ? 0 : progress,
        signal,
      );
      if (signal.aborted || this.state.currentSequenceId !== sequenceId) {
        recovered.playable.destroy();
        return;
      }
      const recoveredOptionIndex = nextOptionIndex + recovered.optionIndex;
      if (resolveStartingPosition) {
        recovered.playable.setPosition(
          resolveStartingPosition({
            playable: recovered.playable,
            usingFallbackSequence: recovered.usingFallbackSequence,
            previous: {
              duration,
              getOffsetForTrack: (index) => previousTrackOffsets[index] ?? duration,
              position: currentPosition,
              usingFallbackSequence: previousUsingFallbackSequence,
            },
          }),
        );
      }
      this.activatePlayable(
        recovered.playable,
        sourceOptions,
        recoveredOptionIndex,
        signal,
        sequenceId,
        resolveStartingPosition,
      );
      if (resumePlayback) await this.play();
    } catch (recoveryError) {
      if (signal.aborted || this.state.currentSequenceId !== sequenceId) return;
      this.state.loading = false;
      this.state.playable = null;
      this.state.error = recoveryError instanceof Error ? recoveryError : new Error(String(recoveryError));
      this.notify();
    }
  }

  async loadSequence(
    tracks: Array<LoadableTrack>,
    startingProgress = 0,
    sequenceFallbackTracks?: Array<LoadableTrack>,
    resolveStartingPosition?: StartingPositionResolver,
  ): Promise<Playable | null> {
    const { fallbackSourceOptions, sequenceId, sources } = buildSequencePlan(tracks, sequenceFallbackTracks);

    if (this.state.currentSequenceId === sequenceId && this.state.playable) {
      return this.state.playable;
    }

    if (this.pendingSequenceId === sequenceId && this.loadingLock) {
      return await this.loadingLock;
    }

    // The controller also owns recovery work for the active playable. Always
    // abort it before starting a different sequence so old audio cannot win a
    // navigation/reload race while its fallback is initializing.
    this.abortController?.abort();

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.pendingSequenceId = sequenceId;
    this.state.usingFallbackSequence = false;
    this.state.loading = true;
    this.state.error = null;
    this.notify();

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: sequence teardown, abort, fallback, and callback wiring form one atomic lifecycle.
    this.loadingLock = (async () => {
      try {
        if (this.state.playable) {
          this.state.playable.destroy();
        }

        if (signal.aborted) {
          return null;
        }

        const sourceOptions = [{ sources, usingFallbackSequence: false }, ...fallbackSourceOptions];
        const initialized = await this.initializePlayable(
          sourceOptions,
          resolveStartingPosition ? 0 : startingProgress,
          signal,
        );
        const { playable } = initialized;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (signal.aborted) {
          playable.destroy();
          return null;
        }

        if (resolveStartingPosition) {
          playable.setPosition(
            resolveStartingPosition({
              playable,
              usingFallbackSequence: initialized.usingFallbackSequence,
            }),
          );
        }

        this.activatePlayable(
          playable,
          sourceOptions,
          initialized.optionIndex,
          signal,
          sequenceId,
          resolveStartingPosition,
        );

        return playable;
      } catch (error) {
        if (signal.aborted) {
          return null;
        }

        const resolvedError = error instanceof Error ? error : new Error(String(error));
        console.error("[PlayerEngine] Failed to load sequence:", resolvedError.message, {
          sources,
          error: resolvedError,
        });
        this.state.loading = false;
        this.state.playable = null;
        this.state.currentSequenceId = null;
        this.state.error = resolvedError;
        this.notify();
        return null;
      } finally {
        this.loadingLock = null;
        this.pendingSequenceId = null;
      }
    })();

    return await this.loadingLock;
  }

  async play(fromPosition?: number | undefined) {
    const { playable } = this.state;
    if (!playable || this.state.loading) return;

    if (this.state.ended) {
      playable.setPosition(0);
      this.state.ended = false;
      this.notify();
    }

    try {
      if (fromPosition !== undefined) {
        this.setPosition(fromPosition);
        this.notify();
      }

      await playable.play();

      this.state.playing = true;
      this.notify();
    } catch (error) {
      this.state.playing = false;
      this.notify();

      if (error instanceof DOMException && error.name === "NotAllowedError") {
        // Handle autoplay / permission issue
        console.warn("Playback blocked by browser:", error);

        // Example: mark state so UI can show "Tap to play"
        this.notify();

        return; // swallow it if this is expected
      }

      // Unexpected error → still bubble up
      throw error;
    }
  }

  pause() {
    const { playable } = this.state;
    if (!playable) return;

    playable.pause();
    this.state.playing = false;
    this.notify();
  }

  setPosition(position: number) {
    const { playable } = this.state;
    if (!playable) return;

    playable.setPosition(position);
    this.notify();
  }

  setVolume(volume: number) {
    const nextVolume = Math.min(Math.max(volume, 0), 1);

    this.state.volume = nextVolume;
    this.state.playable?.setVolume(nextVolume);
    this.notify();
  }

  getPosition(): number {
    return this.state.playable?.getPosition() ?? 0;
  }

  getPlayable() {
    return this.state.playable;
  }

  isUsingFallbackSequence() {
    return this.state.usingFallbackSequence;
  }

  reset() {
    if (this.state.playable) {
      this.state.playable.destroy();
    }
    this.abortController?.abort();
    this.pendingSequenceId = null;
    this.state = {
      playable: null,
      duration: 0,
      volume: this.state.volume,
      playing: false,
      ended: false,
      loading: false,
      currentSequenceId: null,
      usingFallbackSequence: false,
      error: null,
    };
    this.notify();
  }
}

export const playerEngine = new PlayerEngine();
